/*
 * Copyright (c) 2026. Tooling Team
 */

import PropTypes from "prop-types";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Card } from "@uhg-abyss/web/ui/Card";
import { Drawer } from "@uhg-abyss/web/ui/Drawer";
import { I18nProvider } from "@uhg-abyss/web/ui/I18nProvider";
import { Timeline } from "@uhg-abyss/web/ui/Timeline";
import { useGetLabDomainCodeMappingHistoryQuery } from "../../../../app/services/labDomainApi";
import { formatConceptDetailValue } from "../utils/formatters";
import { getMappingHistoryTimelineItems } from "../utils/history";
import ConfidenceScore from "./ConfidenceScore";

const DETAIL_CARDS_STYLE = {
  display: "grid",
  gap: "16px",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
};

const FIELD_STYLE = {
  marginBottom: "12px",
};

const LABEL_STYLE = {
  color: "#4b5563",
  display: "block",
  fontSize: "12px",
  fontWeight: 700,
  marginBottom: "3px",
  textTransform: "uppercase",
};

const VALUE_STYLE = {
  overflowWrap: "anywhere",
};

const DRAWER_CONTENT_STYLE = {
  padding: "16px",
};

const CARD_CONTENT_STYLE = {
  padding: "16px",
};

const CARD_TITLE_STYLE = {
  fontSize: "18px",
  fontWeight: 700,
  lineHeight: 1.3,
  margin: "0 0 16px",
};

const CONFIDENCE_OVERLAP_STYLE = {
  margin: "-56px 0 24px",
  position: "relative",
  zIndex: 1,
};

const TIMELINE_TRANSLATIONS = {
  Timeline: {
    currentStep: "Current history",
    step: "{{stepTitle}}",
    stepCount: "History {{activeStep}} of {{stepCount}}",
  },
};

const TIMELINE_CSS = {
  "abyss-timeline-step-count": {
    display: "none",
  },
};

const Field = ({ label, value }) => (
  <div style={FIELD_STYLE}>
    <span style={LABEL_STYLE}>{label}</span>
    <span style={VALUE_STYLE}>{formatConceptDetailValue(value)}</span>
  </div>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
};

const getStandardCode = (row) =>
  row?.loincConceptCode ?? row?.loincConcept?.code ?? row?.loincCode;

const getStandardDescription = (row) =>
  row?.loincConceptDescription ??
  row?.loincConcept?.description ??
  row?.loincLongCommonName;

const MappingHistoryDrawer = ({ isOpen, onClose, row }) => {
  const recordId = row?._id;
  const {
    data: historyRecords = [],
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  } = useGetLabDomainCodeMappingHistoryQuery(recordId, {
    skip: !recordId || !isOpen,
  });
  const isPending = isLoading || isFetching;
  const timelineItems = getMappingHistoryTimelineItems(historyRecords);
  const errorMessage =
    error?.data?.message || error?.error || "Unable to load mapping history.";

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      position="right"
      scrollableFocus
      size="600px"
      header="Mapping History"
    >
      <div style={DRAWER_CONTENT_STYLE}>
        <div style={DETAIL_CARDS_STYLE}>
          <Card>
            <div style={CARD_CONTENT_STYLE}>
              <h3 style={CARD_TITLE_STYLE}>Local</h3>
              <Field label="Code" value={row?.localCode} />
              <Field label="Name" value={row?.localName} />
              <Field label="System" value={row?.localCodeSystem} />
            </div>
          </Card>
          <Card>
            <div style={CARD_CONTENT_STYLE}>
              <h3 style={CARD_TITLE_STYLE}>Standard</h3>
              <Field label="Code" value={getStandardCode(row)} />
              <Field label="Description" value={getStandardDescription(row)} />
              <Field label="Status" value={row?.status} />
            </div>
          </Card>
        </div>

        <ConfidenceScore
          style={CONFIDENCE_OVERLAP_STYLE}
          value={row?.mlProbability}
        />

        {isPending && <p>Loading mapping history...</p>}

        {isError && (
          <div>
            <p>{errorMessage}</p>
            <Button onClick={refetch}>Retry</Button>
          </div>
        )}

        {!isPending && !isError && timelineItems.length === 0 && (
          <p>No mapping history changes found.</p>
        )}

        {!isPending && !isError && timelineItems.length > 0 && (
          <I18nProvider translations={TIMELINE_TRANSLATIONS}>
            <Timeline
              title="History"
              activeStep={timelineItems.length}
              css={TIMELINE_CSS}
              type="simple"
            >
              {timelineItems.map((item) => (
                <Timeline.Step
                  date={`by ${item.mapperId} ${item.createdAtDisplay}`}
                  description={`${item.previousValue} to ${item.newValue}.`}
                  hideCompletionState
                  key={item.id}
                  title={`${item.label} changed`}
                />
              ))}
            </Timeline>
          </I18nProvider>
        )}
      </div>
    </Drawer>
  );
};

MappingHistoryDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  row: PropTypes.object,
};

export default MappingHistoryDrawer;
